import { useState } from "react";
import { api } from "../api.js";
import { TapButton } from "./TapButton.js";
import type { ResultMethod } from "@mma/shared";

interface Props {
  fightId: string;
  totalRounds: number;
  currentRound: number;
  fighterAName: string;
  fighterACorner: "red" | "blue";
  fighterBName: string;
  fighterBCorner: "red" | "blue";
  onClose: () => void;
  onFinished: () => void;
}

const METHODS: { value: ResultMethod; label: string }[] = [
  { value: "ko", label: "KO" },
  { value: "tko", label: "TKO" },
  { value: "submission", label: "Submission" },
  { value: "decision_unanimous", label: "Decision — Unanimous" },
  { value: "decision_split", label: "Decision — Split" },
  { value: "decision_majority", label: "Decision — Majority" },
  { value: "draw", label: "Draw" },
  { value: "dq", label: "DQ" },
  { value: "no_contest", label: "No Contest" },
];

export function FinishModal({
  fightId,
  totalRounds,
  currentRound,
  fighterAName,
  fighterACorner,
  fighterBName,
  fighterBCorner,
  onClose,
  onFinished,
}: Props) {
  // Rød fighter altid til højre (matcher broadcast-grafikkerne).
  // Vinder-værdien skal stadig være den oprindelige a/b for API-kaldet.
  const aIsRed = fighterACorner === "red";
  const left = {
    side: aIsRed ? "b" : ("a" as "a" | "b"),
    name: aIsRed ? fighterBName : fighterAName,
    corner: aIsRed ? fighterBCorner : fighterACorner,
  };
  const right = {
    side: aIsRed ? "a" : ("b" as "a" | "b"),
    name: aIsRed ? fighterAName : fighterBName,
    corner: aIsRed ? fighterACorner : fighterBCorner,
  };
  const [method, setMethod] = useState<ResultMethod>("decision_unanimous");
  const [round, setRound] = useState<number>(currentRound);
  const [winner, setWinner] = useState<"a" | "b" | "">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDecision = method.startsWith("decision_");
  const noWinner = method === "draw" || method === "no_contest";
  const needsRound = !isDecision && method !== "draw" && method !== "no_contest";
  const needsWinner = !noWinner;

  const canSubmit = !busy && (!needsWinner || winner !== "");

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/fights/${fightId}/finish`, {
        method,
        round: needsRound ? round : isDecision ? totalRounds : undefined,
        winner: needsWinner ? winner : undefined,
      });
      onFinished();
    } catch {
      setError("Kunne ikke afslutte kamp. Prøv igen.");
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-neutral-900 border border-neutral-800 rounded-2xl p-5 max-w-md w-full space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold">Afslut kamp</h2>

        <label className="flex flex-col gap-1 text-xs text-neutral-400">
          Resultat-metode
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value as ResultMethod)}
            className="bg-neutral-800 rounded-lg px-3 py-3 text-base text-neutral-100"
          >
            {METHODS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </label>

        {needsRound && (
          <label className="flex flex-col gap-1 text-xs text-neutral-400">
            I hvilken runde?
            <input
              type="number"
              min={1}
              max={totalRounds}
              value={round}
              onChange={(e) => setRound(Number(e.target.value))}
              className="bg-neutral-800 rounded-lg px-3 py-3 text-base text-neutral-100"
            />
          </label>
        )}

        {needsWinner && (
          <div className="space-y-1">
            <div className="text-xs text-neutral-400">Vinder</div>
            <div className="grid grid-cols-2 gap-2">
              <TapButton
                variant={winner === left.side ? "landed" : "neutral"}
                onClick={() => setWinner(left.side)}
                className="py-4 px-3"
              >
                <span className="block text-xs uppercase opacity-75">
                  {left.corner === "red" ? "Rød" : "Blå"}
                </span>
                {left.name}
              </TapButton>
              <TapButton
                variant={winner === right.side ? "landed" : "neutral"}
                onClick={() => setWinner(right.side)}
                className="py-4 px-3"
              >
                <span className="block text-xs uppercase opacity-75">
                  {right.corner === "red" ? "Rød" : "Blå"}
                </span>
                {right.name}
              </TapButton>
            </div>
          </div>
        )}

        {error && <div className="text-sm text-red-400">{error}</div>}

        <div className="flex gap-2 pt-2">
          <TapButton
            variant="neutral"
            onClick={onClose}
            className="flex-1 py-3"
            disabled={busy}
          >
            Annullér
          </TapButton>
          <TapButton
            variant="warning"
            onClick={submit}
            className="flex-1 py-3"
            disabled={!canSubmit}
          >
            {busy ? "Gemmer…" : "Afslut kamp"}
          </TapButton>
        </div>
      </div>
    </div>
  );
}
